import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InputParameter, InputParameterSchema, KeyValueSchema } from "./constants";
import { z } from "zod";
import { Privacy } from "@/helpers/constants";

export const ConfigVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  config: z.array(InputParameterSchema),
});

export const DatamateIntegrationSchema = z.object({
  name: z.string(),
  id: z.coerce.string(),
  type: z.string(),
  description: z.string(),
  config: z.array(InputParameterSchema).optional(),
  additionalConfigVariants: z.array(ConfigVariantSchema).optional(),
  tools: z
    .array(
      z.object({
        name: z.string(),
        key: z.string(),
        enable_all: z.array(z.string()).nullable().optional(),
      })
    )
    .optional(),
  toolConfig: z.array(InputParameterSchema).or(z.array(KeyValueSchema)).optional(),
  supports_saas_connection_test: z.boolean().default(false),
  supports_local_connection_test: z.boolean().default(false),
});

export type DatamateIntegration = z.infer<typeof DatamateIntegrationSchema>;

export interface CommonIntegrationInstance extends DatamateIntegration {
  inputs?: InputParameter[];
}

export const CommonIntegrationInstanceSchema = DatamateIntegrationSchema.extend({
  inputs: z.array(InputParameterSchema).optional(),
});

export const TeammateSelectedIntegrationSchema = z.object({
  id: z.coerce.string(),
  tools: z.array(
    z.object({
      key: z.string(),
    })
  ),
});

export type TeammateSelectedIntegration = z.infer<typeof TeammateSelectedIntegrationSchema>;

export const TeammateSchema = z.object({
  id: z.coerce.string().optional(),
  name: z.string(),
  description: z.string().optional().nullable(),
  integrations: z.array(TeammateSelectedIntegrationSchema).optional(),
  interfaces: z.array(z.string()).optional(),
  memory_enabled: z.boolean().optional(),
  knowledge_engine_enabled: z.boolean().optional(),
  knowledge_bases: z.array(z.number()).optional().nullable(),
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC]).default(Privacy.PRIVATE).optional(),
  user_id: z.number().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
});

export type Teammate = z.infer<typeof TeammateSchema>;

export const TeammateCreateResponseSchema = z.object({
  id: z.coerce.number(),
});

export type TeammateCreateResponse = z.infer<typeof TeammateCreateResponseSchema>;

export const TeammateUpdateResponseSchema = z.object({
  datamate: TeammateSchema,
});

export type TeammateUpdateResponse = z.infer<typeof TeammateUpdateResponseSchema>;

export const UsageSchema = z.object({
  totals: z.object({
    assists: z.number(),
    guardrails: z.number(),
  }),
  previous_totals: z.object({
    assists: z.number(),
    guardrails: z.number(),
  }),
  datamates_usage: z.record(
    z.string(),
    z.object({
      assists: z.number(),
      guardrails: z.number(),
    })
  ),
});

export type Usage = z.infer<typeof UsageSchema>;

export interface MCPStateProps {
  teammate: Teammate | null;
}

export const ConnectionSchema = z.object({
  id: z.coerce.string().optional(),
  name: z.string(),
  integration_id: z.string(),
  config: z.record(z.string(), z.any()),
  created_at: z.string().optional(),
  updated_at: z.string().nullable(),
});

export type Connection = z.infer<typeof ConnectionSchema>;
export type ConfigVariant = z.infer<typeof ConfigVariantSchema>;

export const initialState: MCPStateProps = {
  teammate: null,
};

const mcpSlice = createSlice({
  name: "mcpState",
  initialState,
  reducers: {
    setTeammate: (state, action: PayloadAction<Teammate>) => {
      state.teammate = action.payload;
    },
    updateTeammate: (state, action: PayloadAction<Partial<Teammate>>) => {
      state.teammate = { ...state.teammate, ...action.payload } as Teammate;
    },
  },
});

export const { setTeammate, updateTeammate } = mcpSlice.actions;
export default mcpSlice;

export const DatamateAuditEventModelSchema = z.object({
  id: z.number(),
  event_type: z.string(),
  user_id: z.number(),
  tool_name: z.string(),
  integration_id: z.string(),
  payload: z.any(),
  teammate_id: z.string(),
  timestamp: z.string(),
  created_at: z.string(),
});

export const GetDatamateWithStatsResponseSchema = z.object({
  datamate: TeammateSchema,
  usage: z.object({
    assists: z.number(),
    guardrails: z.number(),
  }),
  events: z.array(DatamateAuditEventModelSchema),
  created_at: z.string(),
  last_used_at: z.string().nullable(),
  privacy: z.enum([Privacy.PRIVATE, Privacy.PUBLIC]).default(Privacy.PRIVATE).optional(),
  user_id: z.number().optional(),
});

export type DatamateAuditEventModel = z.infer<typeof DatamateAuditEventModelSchema>;
export type GetDatamateWithStatsResponse = z.infer<typeof GetDatamateWithStatsResponseSchema>;

// Aggregation schemas for assist and guardrail endpoints
export const AssistAggregationByIntegrationSchema = z.object({
  integration_type: z.string(),
  assists: z.number(),
});

export const AssistAggregationByToolSchema = z.object({
  tool_call: z.string(),
  assists: z.number(),
});

export const AssistAggregationsResponseSchema = z.array(
  z.union([AssistAggregationByIntegrationSchema, AssistAggregationByToolSchema])
);

export const GuardrailAggregationByIntegrationSchema = z.object({
  integration_type: z.string(),
  guardrails: z.number().optional(),
});

export const GuardrailAggregationByToolSchema = z.object({
  tool_call: z.string(),
  guardrails: z.number().optional(),
});

export const GuardrailAggregationsResponseSchema = z.array(
  z.union([GuardrailAggregationByIntegrationSchema, GuardrailAggregationByToolSchema])
);

export type AssistAggregationByIntegration = z.infer<typeof AssistAggregationByIntegrationSchema>;
export type AssistAggregationByTool = z.infer<typeof AssistAggregationByToolSchema>;
export type AssistAggregationsResponse = z.infer<typeof AssistAggregationsResponseSchema>;
export type GuardrailAggregationByIntegration = z.infer<
  typeof GuardrailAggregationByIntegrationSchema
>;
export type GuardrailAggregationByTool = z.infer<typeof GuardrailAggregationByToolSchema>;
export type GuardrailAggregationsResponse = z.infer<typeof GuardrailAggregationsResponseSchema>;
